/**
 * 儲存方案相關 API
 */

import { apiClient } from './client';
import type {
  SavePlanRequest,
  SavePlanResponse,
  SavedPlanListItem,
  SavedPlan as SavedPlanType,
} from '@/types/api';

// 重新匯出型別供元件使用（使用 camelCase）
export interface SavedPlan {
  id: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
  thumbnail?: string;
  // 完整資料（從 getSavedPlan 取得）
  originalImageBase64?: string;
  preprocessedImageBase64?: string;
  doors?: SavedPlanType['doors'];
  settings?: SavedPlanType['settings'];
  result?: SavedPlanType['result'];
  deskCount?: number;
  styleRenderedImageBase64?: string;
}

// ========================================================================
// 方案 CRUD
// ========================================================================

/**
 * 轉換 API 回應到 camelCase
 */
function transformListItem(item: SavedPlanListItem): SavedPlan {
  return {
    id: item.id,
    name: item.name,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    thumbnail: item.thumbnail,
    deskCount: item.desk_count,
  };
}

/**
 * 取得所有儲存的方案列表
 */
export async function getSavedPlans(): Promise<SavedPlan[]> {
  const response = await apiClient.get<{ plans: SavedPlanListItem[] }>('/api/v1/saved-plans');
  return response.plans.map(transformListItem);
}

/**
 * 轉換完整方案資料到 camelCase
 */
function transformFullPlan(plan: SavedPlanType): SavedPlan {
  return {
    id: plan.id,
    name: plan.metadata?.name ?? '未命名規劃',
    createdAt: plan.metadata?.created_at ?? new Date().toISOString(),
    originalImageBase64: plan.original_image_base64,
    preprocessedImageBase64: plan.preprocessed_image_base64,
    doors: plan.doors,
    settings: plan.settings,
    result: plan.result,
    styleRenderedImageBase64: plan.style_rendered_image_base64,
  };
}

/**
 * 取得特定方案的詳細資料
 */
export async function getSavedPlan(planId: string): Promise<SavedPlan> {
  const response = await apiClient.get<SavedPlanType>(`/api/v1/saved-plans/${planId}`);
  return transformFullPlan(response);
}

/**
 * 儲存新方案
 */
export async function savePlan(data: SavePlanRequest): Promise<SavePlanResponse> {
  return apiClient.post<SavePlanResponse>('/api/v1/saved-plans', {
    body: data,
  });
}

/**
 * 更新現有方案
 */
export async function updatePlan(
  planId: string,
  data: Partial<SavePlanRequest>
): Promise<SavePlanResponse> {
  return apiClient.put<SavePlanResponse>(`/api/v1/saved-plans/${planId}`, {
    body: data,
  });
}

/**
 * 刪除方案
 */
export async function deletePlan(planId: string): Promise<void> {
  await apiClient.delete(`/api/v1/saved-plans/${planId}`);
}

/** 刪除方案（別名，供元件使用） */
export const deleteSavedPlan = deletePlan;

// ========================================================================
// 方案匯出
// ========================================================================

/**
 * 匯出方案為 JSON
 */
export function exportPlanAsJson(plan: SavedPlan): Blob {
  const json = JSON.stringify(plan, null, 2);
  return new Blob([json], { type: 'application/json' });
}

// 匯入功能暫時停用，待後續實作從後端載入方案後再啟用
// export async function importPlanFromJson(file: File): Promise<SavePlanRequest> { ... }
